import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Check } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import ShareCTA from "@/components/ShareCTA";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import useLanguage from "@/hooks/useLanguage";
import { scrollToId } from "@/utils/scroll";

export default function Header() {
  const { t } = useTranslation();
  const { current, locales, selectLocale } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const code = current.code;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleAbout = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToId("about-the-program");
  };

  return (
    <header className="site-header" data-testid="site-header">
      <div className="site-header__inner">
        <Wordmark variant="light" />

        <nav className="site-header__nav" aria-label="Primary">
          <a
            href="#about-the-program"
            className="nav-link"
            onClick={handleAbout}
            data-testid="nav-link-about"
          >
            {t("common.nav.about")}
          </a>
          <ShareCTA className="nav-link" data-testid="nav-link-share" aria-label={`${t("common.nav.share")} ${t("common.newTabSuffix")}`}>
            {t("common.nav.share")}
          </ShareCTA>

          <LanguageSwitcher />
        </nav>

        <button
          type="button"
          className="site-header__menu-btn"
          aria-label={menuOpen ? t("common.menu.close") : t("common.menu.open")}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {menuOpen ? (
            <X size={24} strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <Menu size={24} strokeWidth={1.5} aria-hidden="true" />
          )}
        </button>
      </div>

      {menuOpen && (
        <div className="mobile-menu" id="mobile-menu" data-testid="mobile-menu-panel">
          <nav className="mobile-menu__nav" aria-label="Mobile">
            <a
              href="#about-the-program"
              className="mobile-menu__link"
              onClick={handleAbout}
              data-testid="mobile-nav-link-about"
            >
              {t("common.nav.about")}
            </a>
            <ShareCTA
              className="mobile-menu__link"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-nav-link-share"
              aria-label={`${t("common.nav.share")} ${t("common.newTabSuffix")}`}
            >
              {t("common.nav.share")}
            </ShareCTA>

            <div
              className={`mobile-menu__langs${
                locales.length > 6 ? " mobile-menu__langs--grid" : ""
              }`}
              role="group"
              aria-label={t("common.langSwitcher.aria")}
              data-testid="mobile-lang-list"
            >
              {locales.map((l) => {
                const active = l.code === code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    className={`mobile-menu__lang${active ? " is-active" : ""}`}
                    aria-current={active ? "true" : undefined}
                    onClick={() => {
                      setMenuOpen(false);
                      selectLocale(l.code);
                    }}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    <span
                      className={`fi fi-${l.flag.toLowerCase()} switcher-flag`}
                      aria-hidden="true"
                    />
                    <span className="mobile-menu__lang-name">{l.name}</span>
                    {active && (
                      <Check
                        size={16}
                        strokeWidth={1.5}
                        className="mobile-menu__lang-check"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
